import type { StateCreator } from "zustand";
import { t as translate, type LocaleParams } from "@magic-md/i18n";
import type {
  SessionPaneLayout,
  SessionPaneState,
  SessionState,
  StarredEntry,
  StarredKind,
  WorkspaceReferenceImpactOperation,
  WorkspaceReferenceImpactResult,
  WorkspaceReferenceRewrite,
} from "@magic-md/engine-client/protocol";
import {
  type ConfirmDialogDetailItem,
  showConfirmDialog,
  showFilePickerDialog,
  showFolderPickerDialog,
  showSaveDialog,
} from "../../components/dialogs";
import { fileNameFromPath } from "../../lib/path-utils";
import type { WorkspacePinnedUnit } from "../../lib/pinned-units";
import { normalizePreviewWidth } from "../../lib/preview-width";
import { getPlatformService } from "../../platform";
import { frontendLog } from "../../utils/frontend-logger";
import { useRecentStore, type RecentItem } from "../recent-store";
import { useBookmarkStore } from "../bookmark-store";
import {
  clearPinnedUnitSessions,
  pinnedUnitSessionRegistry,
} from "../pinned-unit-sessions";
import { scheduleSessionPersist } from "./tabs-slice";
import type { EditorState, FileTreeNode, WorkspaceSlice } from "./types";
import {
  getBundleDirFromPublishDirPath,
  getAssetRefFromPath,
  getAssetRelativePathFromPath,
  getDocumentBundleDir,
  getDocumentPathFromAssetsPath,
  isDocumentPublishDirPath,
  isDocumentAssetsPath,
  isFolderPublishDirPath,
  hasFolderPublishMetadataEntries,
} from "../../lib/publish-utils";
import {
  buildOutputWatchVirtualChildren,
  isDocumentWatchesPath,
} from "../../lib/output-watch-persistence";
import {
  buildBundleVirtualChildren,
  buildVirtualChildren,
  buildVisibleChildren,
  buildPublishVirtualChildren,
  buildFolderPublishVirtualChildren,
  getPaneLayoutLeafIds,
  isPaneCollapsedInLayout,
  isExpandableNode,
  reloadSettingsContext,
  type DirEntry,
} from "./utils";

function tr(key: string, params?: LocaleParams): string {
  return translate("editor", key, params);
}

type WorkspaceEngine = ReturnType<typeof getPlatformService>["engine"];
type WorkspaceSessionState = SessionState & {
  pinnedUnits?: WorkspacePinnedUnit[];
};

interface PlannedMove {
  srcPath: string;
  destPath: string;
  name: string;
  parentDir: string;
  isDirectory: boolean;
  pairedBundle?: { from: string; to: string };
}

async function safeReadDir(
  engine: WorkspaceEngine,
  dirPath: string,
): Promise<DirEntry[]> {
  try {
    const { entries } = await engine.workspaceReadDir({
      path: dirPath,
      allowMissing: true,
    });
    return entries;
  } catch {
    return [];
  }
}

async function isReferenceIntegrityEnabled(
  state: EditorState,
  engine: WorkspaceEngine,
): Promise<boolean> {
  if (state.currentWorkspaceFile?.toLowerCase().endsWith(".mdspace")) {
    return true;
  }
  for (const folder of state.workspaceFolders) {
    const candidates = [
      `${folder.path}.mdspace`,
      `${folder.path}/workspace.mdspace`,
    ];
    for (const candidate of candidates) {
      try {
        if ((await engine.workspaceExists({ path: candidate })).exists) {
          return true;
        }
      } catch {
        // Candidate may be outside the allowed roots for this engine mode.
      }
    }
  }
  return false;
}

async function getReferenceImpact(
  state: EditorState,
  operations: WorkspaceReferenceImpactOperation[],
): Promise<WorkspaceReferenceImpactResult | null> {
  const engine = getPlatformService().engine;
  if (operations.length === 0 || !(await isReferenceIntegrityEnabled(state, engine))) {
    return null;
  }
  try {
    return await engine.workspaceReferenceImpact({ operations });
  } catch (err) {
    frontendLog.error("Failed to analyze workspace reference impact", err, {
      source: "system",
    });
    return null;
  }
}

function formatWorkspaceRelativePath(state: EditorState, filePath: string): string {
  const folder = state.workspaceFolders
    .filter((entry) => filePath === entry.path || filePath.startsWith(`${entry.path}/`))
    .sort((a, b) => b.path.length - a.path.length)[0];
  if (!folder) return filePath;
  const suffix = filePath === folder.path ? "" : filePath.slice(folder.path.length + 1);
  return suffix ? `${folder.name}/${suffix}` : folder.name;
}

function buildReferenceDetailItems(
  state: EditorState,
  impact: WorkspaceReferenceImpactResult,
): ConfirmDialogDetailItem[] {
  const shown: ConfirmDialogDetailItem[] = impact.references.slice(0, 8).map((ref) => {
    const pathLabel = formatWorkspaceRelativePath(state, ref.documentPath);
    return {
      title: `${pathLabel}:${ref.range.startLine}`,
      meta: ref.kind === "import-source" ? "import" : "assets",
      description: ref.newText
        ? `${ref.oldText} -> ${ref.newText}`
        : ref.preview,
      tone: ref.newText ? "default" : "warning",
    } satisfies ConfirmDialogDetailItem;
  });
  const remaining = impact.references.length - shown.length;
  if (remaining > 0) {
    shown.push({
      title: tr("workspace.references.remaining.title", { count: remaining }),
      description: tr("workspace.references.remaining.description"),
    });
  }
  return shown;
}

async function confirmReferenceUpdate(
  state: EditorState,
  impact: WorkspaceReferenceImpactResult | null,
  actionLabel: string,
): Promise<boolean> {
  if (!impact || impact.references.length === 0) return true;

  const detailItems = buildReferenceDetailItems(state, impact);
  if (!impact.canAutoApply) {
    return showConfirmDialog(
      tr("workspace.references.updateUnsafe.message", {
        action: actionLabel,
        count: impact.references.length,
      }),
      {
        title: tr("workspace.references.impactTitle"),
        confirmLabel: tr("workspace.references.stillAction", { action: actionLabel }),
        destructive: true,
        detailItems,
        footerNote: tr("workspace.references.footerNote"),
      },
    );
  }

  return showConfirmDialog(
    tr("workspace.references.updateSafe.message", {
      action: actionLabel,
      count: impact.references.length,
    }),
    {
      title: tr("workspace.references.updateTitle"),
      confirmLabel: tr("workspace.references.actionAndUpdate", { action: actionLabel }),
      detailItems,
    },
  );
}

async function confirmReferenceDelete(
  state: EditorState,
  impact: WorkspaceReferenceImpactResult | null,
  name: string,
  label: string,
): Promise<boolean> {
  if (!impact || impact.references.length === 0) {
    return showConfirmDialog(
      tr("workspace.confirmDelete.message", { label, name }),
      {
        title: tr("workspace.confirmDelete.title", { label }),
        confirmLabel: tr("common.delete"),
        destructive: true,
      },
    );
  }

  return showConfirmDialog(
    tr("workspace.confirmDeleteReferenced.message", {
      label,
      name,
      count: impact.references.length,
    }),
    {
      title: tr("workspace.confirmDeleteReferenced.title"),
      confirmLabel: tr("workspace.confirmDeleteReferenced.confirm"),
      destructive: true,
      detailItems: buildReferenceDetailItems(state, impact),
      footerNote: tr("workspace.confirmDeleteReferenced.footer"),
    },
  );
}

async function applyReferenceRewrites(
  state: EditorState,
  rewrites: WorkspaceReferenceRewrite[],
): Promise<number> {
  const applicable = rewrites.filter((rewrite) => rewrite.newText !== undefined);
  if (applicable.length === 0) return 0;

  const engine = getPlatformService().engine;
  const byDocument = new Map<string, WorkspaceReferenceRewrite[]>();
  for (const rewrite of applicable) {
    const group = byDocument.get(rewrite.documentPath) ?? [];
    group.push(rewrite);
    byDocument.set(rewrite.documentPath, group);
  }

  let applied = 0;
  for (const [documentPath, documentRewrites] of byDocument) {
    const openTab = state.tabs.find((tab) => tab.filePath === documentPath);
    const originalContent = openTab?.content ??
      (await engine.workspaceReadFile({ path: documentPath })).content;
    const result = applyRewritesToContent(originalContent, documentRewrites);
    if (result.content === originalContent) {
      applied += result.applied;
      continue;
    }
    if (openTab) {
      state.setContentImmediate(result.content, openTab.id);
    } else {
      await engine.workspaceWriteFile({ path: documentPath, content: result.content });
    }
    applied += result.applied;
  }
  return applied;
}

async function applyLegacyAssetRenameReferences(
  state: EditorState,
  nodePath: string,
  newPath: string,
): Promise<number> {
  const oldAssetRef = getAssetRefFromPath(nodePath);
  const newAssetRef = getAssetRefFromPath(newPath);
  const oldAssetRelativePath = getAssetRelativePathFromPath(nodePath);
  const newAssetRelativePath = getAssetRelativePathFromPath(newPath);
  const assetDocumentPath = getDocumentPathFromAssetsPath(nodePath);
  if (!assetDocumentPath) return 0;

  const engine = getPlatformService().engine;
  const openTab = state.tabs.find((tab) => tab.filePath === assetDocumentPath);
  const originalContent = openTab?.content ??
    (await engine.workspaceReadFile({ path: assetDocumentPath })).content;
  let nextContent = originalContent;
  if (oldAssetRef && newAssetRef) {
    nextContent = nextContent.split(oldAssetRef).join(newAssetRef);
  }
  if (oldAssetRelativePath && newAssetRelativePath) {
    nextContent = nextContent
      .split(`$ref{assets/${oldAssetRelativePath}/`)
      .join(`$ref{assets/${newAssetRelativePath}/`);
  }
  if (nextContent === originalContent) return 0;
  if (openTab) {
    state.setContentImmediate(nextContent, openTab.id);
  } else {
    await engine.workspaceWriteFile({
      path: assetDocumentPath,
      content: nextContent,
    });
  }
  return 1;
}

function applyRewritesToContent(
  content: string,
  rewrites: WorkspaceReferenceRewrite[],
): { content: string; applied: number } {
  const withOffsets = rewrites
    .map((rewrite) => {
      const start = offsetAt(content, rewrite.replaceRange.startLine, rewrite.replaceRange.startColumn);
      const end = offsetAt(content, rewrite.replaceRange.endLine, rewrite.replaceRange.endColumn);
      if (start === null || end === null || rewrite.newText === undefined) return null;
      return { rewrite, start, end };
    })
    .filter((item): item is { rewrite: WorkspaceReferenceRewrite; start: number; end: number } => item !== null)
    .sort((a, b) => b.start - a.start);

  let nextContent = content;
  let applied = 0;
  for (const item of withOffsets) {
    if (nextContent.slice(item.start, item.end) !== item.rewrite.oldText) {
      frontendLog.warn("Skipped stale reference rewrite", {
        documentPath: item.rewrite.documentPath,
        oldText: item.rewrite.oldText,
      }, { source: "system" });
      continue;
    }
    nextContent = `${nextContent.slice(0, item.start)}${item.rewrite.newText}${nextContent.slice(item.end)}`;
    applied++;
  }
  return { content: nextContent, applied };
}

function offsetAt(content: string, line: number, column: number): number | null {
  if (line < 1 || column < 0) return null;
  let currentLine = 1;
  let lineStart = 0;
  for (let i = 0; i < content.length && currentLine < line; i++) {
    if (content[i] === "\n") {
      currentLine++;
      lineStart = i + 1;
    }
  }
  if (currentLine !== line) return null;
  const offset = lineStart + column;
  return offset <= content.length ? offset : null;
}

async function loadNodeChildren(
  engine: WorkspaceEngine,
  node: FileTreeNode,
): Promise<FileTreeNode[]> {
  if (!node.isDirectory && node.bundlePath) {
    const entries = await safeReadDir(engine, node.bundlePath);
    return buildBundleVirtualChildren(node.bundlePath, entries);
  }

  if (node.isVirtual && isDocumentPublishDirPath(node.path)) {
    const bundlePath = getBundleDirFromPublishDirPath(node.path);
    if (!bundlePath) return [];
    const publishEntries = await safeReadDir(engine, node.path);
    return buildPublishVirtualChildren(
      bundlePath,
      publishEntries,
    );
  }

  if (node.isVirtual && isDocumentWatchesPath(node.path)) {
    return buildOutputWatchVirtualChildren(engine, node.path);
  }

  // Folder-level publish virtual node (e.g. <folder>/.magic-md/publish)
  if (node.isVirtual && isFolderPublishDirPath(node.path)) {
    const { entries } = await engine.workspaceReadDir({ path: node.path });
    return buildFolderPublishVirtualChildren(node.path, entries);
  }

  if (node.isVirtual) {
    const { entries } = await engine.workspaceReadDir({ path: node.path });
    return buildVirtualChildren(node.path, entries);
  }

  const { entries } = await engine.workspaceReadDir({ path: node.path });
  const children = buildVisibleChildren(node.path, entries);

  // Inject virtual publish node if folder-level .magic-md/publish has metadata
  const magicMdPublishPath = `${node.path}/.magic-md/publish`;
  try {
    const publishEntries = await safeReadDir(engine, magicMdPublishPath);
    if (hasFolderPublishMetadataEntries(publishEntries)) {
      // Hide real .magic-md dir and inject virtual publish node
      const filtered = children.filter(
        (c) => c.name !== ".magic-md",
      );
      filtered.push({
        name: "publish",
        path: magicMdPublishPath,
        isDirectory: true,
        isVirtual: true,
        children: [],
        loaded: false,
        expanded: false,
      });
      filtered.sort((a, b) => {
        if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
        return a.name.localeCompare(b.name);
      });
      return filtered;
    }
  } catch {
    // .magic-md/publish doesn't exist, skip
  }
  return children;
}

function collectExpandedTreePaths(nodes: FileTreeNode[]): string[] {
  const paths: string[] = [];
  for (const node of nodes) {
    if (isExpandableNode(node) && node.expanded) {
      paths.push(node.path);
    }
    if (node.children?.length) {
      paths.push(...collectExpandedTreePaths(node.children));
    }
  }
  return paths;
}

function applyKnownExpansionState(
  nodes: FileTreeNode[],
  expandedPaths: Set<string>,
): FileTreeNode[] {
  return nodes.map((node) => ({
    ...node,
    ...(isExpandableNode(node)
      ? { expanded: expandedPaths.has(node.path) }
      : {}),
    children: node.children
      ? applyKnownExpansionState(node.children, expandedPaths)
      : node.children,
  }));
}

async function expandTreePath(
  nodes: FileTreeNode[],
  nodePath: string,
): Promise<{ nodes: FileTreeNode[]; changed: boolean }> {
  let changed = false;
  const nextNodes: FileTreeNode[] = [];
  for (const node of nodes) {
    if (node.path === nodePath) {
      if (!isExpandableNode(node)) {
        nextNodes.push(node);
        continue;
      }
      let children = node.children ?? [];
      let loaded = node.loaded;
      if (!loaded) {
        try {
          children = await loadNodeChildren(getPlatformService().engine, node);
          loaded = true;
        } catch (err) {
          frontendLog.error("Failed to restore explorer expansion", err, {
            source: "system",
          });
        }
      }
      const nextNode = {
        ...node,
        expanded: true,
        loaded,
        children,
      };
      changed =
        changed ||
        node.expanded !== nextNode.expanded ||
        node.loaded !== nextNode.loaded ||
        node.children !== nextNode.children;
      nextNodes.push(nextNode);
      continue;
    }

    if (isExpandableNode(node) && node.children?.length) {
      const result = await expandTreePath(node.children, nodePath);
      changed = changed || result.changed;
      nextNodes.push(result.changed ? { ...node, children: result.nodes } : node);
      continue;
    }

    nextNodes.push(node);
  }
  return { nodes: nextNodes, changed };
}

async function restoreExplorerExpansion(
  get: () => EditorState,
  set: (patch: Partial<EditorState>) => void,
  expandedPaths: string[],
): Promise<void> {
  const validPaths = expandedPaths.filter(
    (path): path is string => typeof path === "string" && path.length > 0,
  );
  const expandedSet = new Set(validPaths);
  set({
    fileTree: applyKnownExpansionState(get().fileTree, expandedSet),
  });

  const orderedPaths = [...expandedSet].sort((a, b) => a.length - b.length);
  for (const path of orderedPaths) {
    const result = await expandTreePath(get().fileTree, path);
    if (result.changed) {
      set({ fileTree: result.nodes });
    }
  }
}

export const createWorkspaceSlice: StateCreator<
  EditorState,
  [],
  [],
  WorkspaceSlice
> = (set, get) => ({
  workspaceFolders: [],
  workspacePath: null,
  fileTree: [],
  _clipboardSource: null,
  currentWorkspaceFile: null,
  starred: [],
  lastSession: null,
  isRestoringSession: false,

  /* ── Workspace / Explorer ── */
  hydrateWorkspaceFromEngine: async () => {
    const engine = getPlatformService().engine;
    const [folderResult, starResult, bookmarkResult] = await Promise.all([
      engine.workspaceListFolders(),
      engine.workspaceStarList(),
      engine.workspaceBookmarkList(),
    ]);
    const folders = Array.isArray(folderResult.folders)
      ? folderResult.folders
      : [];
    const starred = Array.isArray(starResult.items) ? starResult.items : [];
    const bookmarks = Array.isArray(bookmarkResult.items)
      ? bookmarkResult.items
      : [];

    set({
      workspaceFolders: folders,
      workspacePath: folders[0]?.path ?? null,
      fileTree: [],
      starred,
    });
    useBookmarkStore.getState()._setBookmarks(bookmarks);
    if (folders.length > 0) {
      await get().refreshFileTree();
    }
  },

  openFolder: async () => {
    const openRecentWorkspace = async (item: RecentItem) => {
      if (item.type === "workspace" || item.path.toLowerCase().endsWith(".mdspace")) {
        await get().loadWorkspaceFromPath(item.path);
        return;
      }
      await get().openFolderFromPath(item.path);
    };

    const dirPath = await showFolderPickerDialog({
      title: tr("dialog.openFolder"),
      onOpenRecentWorkspace: openRecentWorkspace,
    });
    if (!dirPath) return;
    await get().openFolderFromPath(dirPath);
  },

  openFolderFromPath: async (dirPath: string) => {
    if (!dirPath) return;
    const engine = getPlatformService().engine;
    const currentFolders = get().workspaceFolders;
    const alreadyPresent = currentFolders.some(
      (folder) => folder.path === dirPath,
    );

    try {
      if (!alreadyPresent) {
        const result = await engine.workspaceAddFolder({ path: dirPath });
        if (result.success === false) {
          throw new Error(tr("workspace.error.openFolderEngine"));
        }
      }
      for (const folder of currentFolders) {
        if (folder.path === dirPath) continue;
        try {
          await engine.workspaceRemoveFolder({ path: folder.path });
        } catch {
          /* ignore */
        }
      }

      const nextFolder = { path: dirPath, name: fileNameFromPath(dirPath) };
      set({
        workspaceFolders: [nextFolder],
        workspacePath: dirPath,
        fileTree: [],
        sidebarOpen: true,
        sidebarView: "explorer",
      });
      await get().refreshFileTree();
      await reloadSettingsContext();
      useRecentStore.getState().addRecentFolder(dirPath);
      get().setStatusMessage(tr("workspace.status.folderOpened", { name: fileNameFromPath(dirPath) }));
    } catch (err) {
      frontendLog.error("Failed to open folder", err, { source: "system" });
      const errMsg = err instanceof Error ? err.message : String(err);
      get().setStatusMessage(tr("workspace.status.folderOpenFailed", { message: errMsg.slice(0, 80) }));
    }
  },

  addWorkspaceFolder: async (folderPath: string, name?: string) => {
    const engine = getPlatformService().engine;
    try {
      await engine.workspaceAddFolder({ path: folderPath, name });
      await reloadSettingsContext();
      set({ sidebarOpen: true, sidebarView: "explorer" });
      get().setStatusMessage(tr("workspace.status.folderAdded", { name: fileNameFromPath(folderPath) }));
    } catch (err) {
      frontendLog.error("Failed to add workspace folder", err, {
        source: "system",
      });
      const errMsg = err instanceof Error ? err.message : String(err);
      get().setStatusMessage(tr("workspace.status.folderAddFailed", { message: errMsg.slice(0, 80) }));
    }
  },

  removeWorkspaceFolder: async (folderPath: string) => {
    const engine = getPlatformService().engine;
    try {
      await engine.workspaceRemoveFolder({ path: folderPath });
      await reloadSettingsContext();
      get().setStatusMessage(tr("workspace.status.folderRemoved", { name: fileNameFromPath(folderPath) }));
    } catch (err) {
      frontendLog.error("Failed to remove workspace folder", err, {
        source: "system",
      });
      const errMsg = err instanceof Error ? err.message : String(err);
      get().setStatusMessage(tr("workspace.status.folderRemoveFailed", { message: errMsg.slice(0, 80) }));
    }
  },

  saveWorkspace: async () => {
    const filePath = await showSaveDialog({ defaultName: "workspace.mdspace" });
    if (!filePath) return;
    const engine = getPlatformService().engine;
    try {
      const session = collectSessionState(get);
      await engine.workspaceSave({ path: filePath, session });
      set({ currentWorkspaceFile: filePath, lastSession: session });
      useRecentStore.getState().addRecentWorkspace(filePath);
      get().setStatusMessage(tr("workspace.status.saved", { name: fileNameFromPath(filePath) }));
    } catch (err) {
      frontendLog.error("Failed to save workspace", err, { source: "system" });
      const errMsg = err instanceof Error ? err.message : String(err);
      get().setStatusMessage(tr("workspace.status.saveFailed", { message: errMsg.slice(0, 80) }));
    }
  },

  loadWorkspace: async () => {
    const openRecentWorkspace = async (item: RecentItem) => {
      if (item.type === "workspace" || item.path.toLowerCase().endsWith(".mdspace")) {
        await get().loadWorkspaceFromPath(item.path);
        return;
      }
      await get().openFolderFromPath(item.path);
    };

    const filePath = await showFilePickerDialog({
      accept: [".mdspace"],
      onOpenRecentWorkspace: openRecentWorkspace,
    });
    if (!filePath) return;
    await get().loadWorkspaceFromPath(filePath);
  },

  loadWorkspaceFromPath: async (filePath: string) => {
    const engine = getPlatformService().engine;
    try {
      set({ isRestoringSession: true });

      // 切换工作区前，先关闭当前所有 tabs 并重置 panes/layout，
      // 避免旧 workspace 的 tabs 残留（旧路径会落在新 workspace 的 allowedPaths 之外）。
      clearPinnedUnitSessions();
      await get().saveAllAndCloseTabs();

      const result = await engine.workspaceLoad({
        path: filePath,
      });
      if (result.success === false) {
        throw new Error(tr("workspace.error.loadWorkspaceEngine"));
      }
      const { folders, starred, bookmarks, session } = result;
      if (!Array.isArray(folders) || folders.length === 0) {
        throw new Error(tr("workspace.error.noFolders"));
      }
      set({
        workspaceFolders: folders,
        workspacePath: folders[0]?.path ?? null,
        fileTree: [],
        currentWorkspaceFile: filePath,
        starred: Array.isArray(starred) ? starred : [],
        lastSession: session,
      });
      useBookmarkStore
        .getState()
        ._setBookmarks(Array.isArray(bookmarks) ? bookmarks : []);
      await get().refreshFileTree();
      if (session?.explorer && Array.isArray(session.explorer.expandedPaths)) {
        await restoreExplorerExpansion(get, set, session.explorer.expandedPaths);
      }
      await reloadSettingsContext();
      set({ sidebarOpen: true, sidebarView: "explorer" });
      useRecentStore.getState().addRecentWorkspace(filePath);
      if (session) {
        await get().restoreSessionState(session);
      }
      get().setStatusMessage(tr("workspace.status.loaded", { name: fileNameFromPath(filePath) }));
    } catch (err) {
      frontendLog.error("Failed to load workspace", err, { source: "system" });
      const errMsg = err instanceof Error ? err.message : String(err);
      get().setStatusMessage(tr("workspace.status.loadFailed", { message: errMsg.slice(0, 80) }));
    } finally {
      set({ isRestoringSession: false });
    }
  },

  closeWorkspace: async () => {
    const engine = getPlatformService().engine;
    const currentFolders = get().workspaceFolders;
    if (currentFolders.length === 0) return;

    try {
      // 关闭所有 tabs（保存 dirty 内容）+ 重置 panes/layout
      // 设 isRestoringSession 抑制 tab 关闭触发的会话自动持久化
      set({ isRestoringSession: true });
      clearPinnedUnitSessions();
      await get().saveAllAndCloseTabs();

      for (const folder of currentFolders) {
        await engine.workspaceRemoveFolder({ path: folder.path });
      }

      const { folders } = await engine.workspaceListFolders();
      set({
        workspaceFolders: folders,
        workspacePath: folders[0]?.path ?? null,
        fileTree: [],
        currentWorkspaceFile: null,
        starred: [],
        lastSession: null,
        pinnedUnits: [],
        activePinnedUnitId: null,
      });
      useBookmarkStore.getState()._setBookmarks([]);
      if (folders.length > 0) {
        await get().refreshFileTree();
      }
      await reloadSettingsContext();
      get().setStatusMessage(tr("workspace.status.closed"));
    } catch (err) {
      frontendLog.error("Failed to close workspace", err, { source: "system" });
      const errMsg = err instanceof Error ? err.message : String(err);
      get().setStatusMessage(tr("workspace.status.closeFailed", { message: errMsg.slice(0, 80) }));
    } finally {
      set({ isRestoringSession: false });
    }
  },

  /* ── Starred (★) ── */

  refreshStarred: async () => {
    try {
      const { items } = await getPlatformService().engine.workspaceStarList();
      set({ starred: Array.isArray(items) ? items : [] });
    } catch (err) {
      frontendLog.warn("Failed to refresh starred items", err, {
        source: "system",
      });
    }
  },

  starItem: async (
    path: string,
    kind: StarredKind = "file",
    label?: string,
  ) => {
    if (!path) return;
    try {
      const { item } = await getPlatformService().engine.workspaceStarAdd({
        path,
        kind,
        label,
      });
      set((s) => {
        const next = s.starred.filter((x) => x.path !== item.path);
        return { starred: [...next, item] };
      });
    } catch (err) {
      frontendLog.error("Failed to star item", err, { source: "system" });
      const errMsg = err instanceof Error ? err.message : String(err);
      get().setStatusMessage(tr("workspace.status.starFailed", { message: errMsg.slice(0, 80) }));
    }
  },

  unstarItem: async (path: string) => {
    if (!path) return;
    try {
      await getPlatformService().engine.workspaceStarRemove({ path });
      set((s) => ({ starred: s.starred.filter((x) => x.path !== path) }));
    } catch (err) {
      frontendLog.error("Failed to unstar item", err, { source: "system" });
    }
  },

  updateStarLabel: async (path: string, label: string) => {
    if (!path) return;
    try {
      const { item } = await getPlatformService().engine.workspaceStarUpdate({
        path,
        label,
      });
      if (!item) return;
      set((s) => ({
        starred: s.starred.map((x) => (x.path === item.path ? item : x)),
      }));
    } catch (err) {
      frontendLog.error("Failed to update star label", err, {
        source: "system",
      });
    }
  },

  isStarred: (path: string) => {
    return get().starred.some((s) => s.path === path);
  },

  _setStarred: (items: StarredEntry[]) => {
    set({ starred: Array.isArray(items) ? items : [] });
  },

  /* ── Session restore / persist ── */

  persistSessionState: async (options?: { persist?: boolean }) => {
    const { isRestoringSession, currentWorkspaceFile } = get();
    if (isRestoringSession) return;
    const session = collectSessionState(get);
    try {
      await getPlatformService().engine.workspaceSessionSet({
        session,
        persist: options?.persist ?? Boolean(currentWorkspaceFile),
      });
      set({ lastSession: session });
    } catch (err) {
      frontendLog.warn("Failed to persist session", err, { source: "system" });
    }
  },

  restoreSessionState: async (session: SessionState) => {
    if (!session) return;
    const engine = getPlatformService().engine;
    const sessionWithPins = session as WorkspaceSessionState;
    const pinnedUnits = Array.isArray(sessionWithPins.pinnedUnits)
      ? sessionWithPins.pinnedUnits.filter(
        (unit): unit is WorkspacePinnedUnit =>
          unit &&
          typeof unit.id === "string" &&
          typeof unit.documentPath === "string" &&
          typeof unit.unitId === "string" &&
          typeof unit.pluginName === "string",
      )
      : [];
    pinnedUnitSessionRegistry.replaceAll(pinnedUnits);
    set({
      pinnedUnits,
      activePinnedUnitId: null,
    });

    if (!Array.isArray(session.tabs) || session.tabs.length === 0) return;
    const tabs = session.tabs.filter(
      (t) => t && typeof t.path === "string" && t.path.length > 0,
    );

    // 恢复全局视图设置（split / editor / preview 等）
    const viewPatch: Partial<EditorState> = {};
    if (session.viewMode) viewPatch.viewMode = session.viewMode;
    if (typeof session.splitPercent === "number")
      viewPatch.splitPercent = session.splitPercent;
    if (session.previewWidth) viewPatch.previewWidth = normalizePreviewWidth(session.previewWidth);
    if (Object.keys(viewPatch).length > 0) set(viewPatch);

    // Open all unique paths first, build path -> tabId map (use last opened id per path).
    const pathToTabId = new Map<string, string>();
    for (const t of tabs) {
      try {
        const { exists } = await engine.workspaceExists({ path: t.path });
        if (!exists) continue;
        await get().openFileFromPath(t.path);
        const opened = get().tabs.find((tab) => tab.filePath === t.path);
        if (opened) pathToTabId.set(t.path, opened.id);
      } catch (err) {
        frontendLog.warn(`Failed to restore tab ${t.path}`, err, {
          source: "system",
        });
      }
    }

    // Reconstruct multi-pane layout if present.
    const sessionPanes = Array.isArray(session.panes) ? session.panes : [];
    if (sessionPanes.length > 0) {
      const newPaneIds: string[] = sessionPanes.map(
        (_, i) => `pane-${i === 0 ? "main" : `restored-${i}-${Date.now()}`}`,
      );

      const builtPanes = sessionPanes
        .map((sp, i) => {
          const ids = (sp.tabPaths ?? [])
            .map((p) => pathToTabId.get(p))
            .filter((x): x is string => Boolean(x));
          if (ids.length === 0) return null;
          const activeId = sp.activeTabPath
            ? (pathToTabId.get(sp.activeTabPath) ?? ids[0])
            : ids[0];
          return {
            paneId: newPaneIds[i],
            tabId: activeId,
            tabIds: ids,
            viewMode: sp.viewMode ?? "split",
            splitPercent: sp.splitPercent ?? 50,
            previewWidth: normalizePreviewWidth(sp.previewWidth ?? "reading"),
            cursorLine: 1,
            cursorCol: 1,
          };
        })
        .filter((x): x is NonNullable<typeof x> => x !== null);

      if (builtPanes.length > 0) {
        // Remap layout paneIndex -> new paneId
        const remap = (
          node: SessionPaneLayout,
        ): import("./types").PaneLayout | null => {
          if (node.type === "leaf") {
            const id = newPaneIds[node.paneIndex];
            if (!id) return null;
            const exists = builtPanes.some((p) => p.paneId === id);
            const collapsed = "collapsed" in node && node.collapsed === true;
            return exists
              ? {
                type: "leaf",
                paneId: id,
                ...(collapsed ? { collapsed: true } : {}),
              }
              : null;
          }
          const children = node.children
            .map(remap)
            .filter((x): x is import("./types").PaneLayout => x !== null);
          if (children.length === 0) return null;
          if (children.length === 1) return children[0];
          return {
            type: node.type,
            children,
            sizes: node.sizes.slice(0, children.length),
            ...(node.collapsed ? { collapsed: true } : {}),
          };
        };

        let nextLayout: import("./types").PaneLayout | null = null;
        if (session.paneLayout) {
          nextLayout = remap(session.paneLayout);
        }
        if (!nextLayout && builtPanes.length === 1) {
          nextLayout = { type: "leaf", paneId: builtPanes[0].paneId };
        }

        const focusIdx = session.focusedPaneIndex ?? 0;
        let focusedPaneId =
          newPaneIds[focusIdx] &&
          builtPanes.some((p) => p.paneId === newPaneIds[focusIdx])
            ? newPaneIds[focusIdx]
            : builtPanes[0].paneId;
        if (nextLayout && isPaneCollapsedInLayout(nextLayout, focusedPaneId)) {
          focusedPaneId = getPaneLayoutLeafIds(nextLayout)[0] ?? builtPanes[0].paneId;
        }
        const focusedPane = builtPanes.find((p) => p.paneId === focusedPaneId);
        set({
          panes: builtPanes,
          paneLayout: nextLayout,
          focusedPaneId,
          activeTabId: focusedPane?.tabId ?? get().activeTabId,
        });
        return;
      }
    }

    // Fallback (no pane info): activate the previously-active tab if still present.
    if (session.activeTabPath) {
      const target = get().tabs.find(
        (tab) => tab.filePath === session.activeTabPath,
      );
      if (target) get().setActiveTab(target.id);
    }
  },

  refreshFileTree: async () => {
    const { workspaceFolders } = get();
    if (workspaceFolders.length === 0) return;
    try {
      const engine = getPlatformService().engine;
      const tree: FileTreeNode[] = [];

      for (const folder of workspaceFolders) {
        const { entries } = await engine.workspaceReadDir({
          path: folder.path,
        });
        const children = buildVisibleChildren(folder.path, entries);

        tree.push({
          name: folder.name ?? fileNameFromPath(folder.path),
          path: folder.path,
          isDirectory: true,
          isWorkspaceRoot: true,
          children,
          loaded: true,
          expanded: true,
        });
      }
      set({ fileTree: tree });
    } catch (err) {
      frontendLog.error("Failed to read directory", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.folderReadFailed"));
    }
  },

  toggleTreeNode: async (nodePath: string) => {
    const { fileTree } = get();

    async function toggleNode(nodes: FileTreeNode[]): Promise<FileTreeNode[]> {
      const result: FileTreeNode[] = [];
      for (const node of nodes) {
        if (node.path === nodePath) {
          const expandable = node.isDirectory || !!node.bundlePath;
          if (expandable) {
            const newExpanded = !node.expanded;
            let children = node.children ?? [];
            if (newExpanded && !node.loaded) {
              try {
                const engine = getPlatformService().engine;
                children = await loadNodeChildren(engine, node);
              } catch (err) {
                frontendLog.error("Failed to read directory", err, {
                  source: "system",
                });
              }
            }
            result.push({
              ...node,
              expanded: newExpanded,
              loaded: true,
              children,
            });
          } else {
            result.push(node);
          }
        } else if ((node.isDirectory || node.bundlePath) && node.children) {
          result.push({
            ...node,
            children: await toggleNode(node.children),
          });
        } else {
          result.push(node);
        }
      }
      return result;
    }

    const newTree = await toggleNode(fileTree);
    set({ fileTree: newTree });
    scheduleSessionPersist(get);
  },

  expandTreeNode: async (nodePath: string) => {
    const result = await expandTreePath(get().fileTree, nodePath);
    if (result.changed) {
      set({ fileTree: result.nodes });
      scheduleSessionPersist(get);
    }
  },

  /* ── Explorer file operations ── */
  createFileInDir: async (dirPath: string, fileName: string) => {
    let finalName = fileName;
    if (!finalName.includes(".")) {
      finalName = `${finalName}.md`;
    }
    const filePath = `${dirPath}/${finalName}`;
    try {
      const engine = getPlatformService().engine;
      const { exists } = await engine.workspaceExists({ path: filePath });
      if (exists) {
        get().setStatusMessage(tr("workspace.status.fileExists", { name: finalName }), 3000);
        return;
      }
      await engine.workspaceWriteFile({ path: filePath, content: "" });
      await get().refreshSubtree(dirPath);
      get().setStatusMessage(tr("workspace.status.fileCreated", { name: finalName }), 2000);
      await get().openFileFromPath(filePath);
    } catch (err) {
      frontendLog.error("Failed to create file", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.fileCreateFailed"));
    }
  },

  createAssetFileInDir: async (dirPath: string, fileName: string) => {
    const finalName = fileName.trim();
    if (!finalName || finalName.includes("/") || finalName.includes("\\")) {
      get().setStatusMessage(tr("workspace.status.fileNameInvalid"), 3000);
      return;
    }
    const filePath = `${dirPath}/${finalName}`;
    try {
      const engine = getPlatformService().engine;
      const { exists } = await engine.workspaceExists({ path: filePath });
      if (exists) {
        get().setStatusMessage(tr("workspace.status.fileExists", { name: finalName }), 3000);
        return;
      }
      await engine.workspaceWriteFile({ path: filePath, content: "" });
      await get().refreshSubtree(dirPath);
      get().setStatusMessage(tr("workspace.status.assetFileCreated", { name: finalName }), 2000);
    } catch (err) {
      frontendLog.error("Failed to create asset file", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.addAssetFileFailed"));
    }
  },

  createAssetFolderInDir: async (dirPath: string, folderName: string) => {
    const finalName = folderName.trim();
    if (!finalName || finalName.includes("/") || finalName.includes("\\")) {
      get().setStatusMessage(tr("workspace.status.folderNameInvalid"), 3000);
      return;
    }
    const folderPath = `${dirPath}/${finalName}`;
    try {
      const engine = getPlatformService().engine;
      const { exists } = await engine.workspaceExists({ path: folderPath });
      if (exists) {
        get().setStatusMessage(tr("workspace.status.folderExists", { name: finalName }), 3000);
        return;
      }
      await engine.workspaceMkdir({ path: folderPath, recursive: true });
      await get().refreshSubtree(dirPath);
      get().setStatusMessage(tr("workspace.status.assetFolderCreated", { name: finalName }), 2000);
    } catch (err) {
      frontendLog.error("Failed to create asset folder", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.addAssetFolderFailed"));
    }
  },

  createFolderInDir: async (dirPath: string, folderName: string) => {
    const folderPath = `${dirPath}/${folderName}`;
    try {
      const engine = getPlatformService().engine;
      const { exists } = await engine.workspaceExists({ path: folderPath });
      if (exists) {
        get().setStatusMessage(tr("workspace.status.folderExists", { name: folderName }), 3000);
        return;
      }
      await engine.workspaceMkdir({ path: folderPath });
      await get().refreshSubtree(dirPath);
      get().setStatusMessage(tr("workspace.status.folderAdded", { name: folderName }), 2000);
    } catch (err) {
      frontendLog.error("Failed to create folder", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.addFolderFailed"));
    }
  },

  deleteNode: async (nodePath: string, isDirectory: boolean, bundlePath?: string) => {
    const name = fileNameFromPath(nodePath);
    const label = isDirectory ? tr("workspace.label.folder") : tr("workspace.label.file");
    const engine = getPlatformService().engine;
    const pairedBundlePath = !isDirectory && /\.md$/i.test(nodePath)
      ? bundlePath ?? getDocumentBundleDir(nodePath)
      : undefined;
    const shouldDeleteBundle = pairedBundlePath
      ? (
        await engine.workspaceExists({ path: pairedBundlePath }).catch(() => ({
          exists: false,
        }))
      ).exists
      : false;
    const referenceOperations: WorkspaceReferenceImpactOperation[] = [
      { type: "delete", from: nodePath, isDirectory },
    ];
    if (pairedBundlePath && shouldDeleteBundle) {
      referenceOperations.push({
        type: "delete",
        from: pairedBundlePath,
        isDirectory: true,
      });
    }
    const impact = await getReferenceImpact(get(), referenceOperations);
    const ok = await confirmReferenceDelete(get(), impact, name, label);
    if (!ok) return false;
    try {
      await engine.workspaceRemove({ path: nodePath });
      if (pairedBundlePath && shouldDeleteBundle) {
        await engine.workspaceRemove({ path: pairedBundlePath });
      }
      const parentDir = nodePath.substring(0, nodePath.lastIndexOf("/"));
      if (parentDir) {
        await get().refreshSubtree(parentDir);
      } else {
        await get().refreshFileTree();
      }
      const openTab = get().tabs.find((t) => t.filePath === nodePath);
      if (openTab) {
        get().closeTab(openTab.id);
      }
      get().setStatusMessage(tr("workspace.status.deleted", { name }), 2000);
      return true;
    } catch (err) {
      frontendLog.error("Failed to delete node", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.deleteFailed", { name }));
      return false;
    }
  },

  renameNode: async (nodePath: string, newName: string) => {
    if (!newName.trim()) return false;
    const parentDir = nodePath.substring(0, nodePath.lastIndexOf("/"));
    const newPath = `${parentDir}/${newName}`;
    try {
      const engine = getPlatformService().engine;
      const sourceStat = await engine.workspaceStat({ path: nodePath }).catch(() => null);
      const isDirectory = sourceStat?.isDirectory ?? false;
      const { exists } = await engine.workspaceExists({ path: newPath });
      if (exists) {
        get().setStatusMessage(tr("workspace.status.targetExists", { name: newName }), 3000);
        return false;
      }
      const pairedBundle = !isDirectory && /\.md$/i.test(nodePath)
        ? {
          from: getDocumentBundleDir(nodePath),
          to: getDocumentBundleDir(newPath),
        }
        : null;
      const shouldRenameBundle = pairedBundle
        ? (await engine.workspaceExists({ path: pairedBundle.from })).exists
        : false;
      if (pairedBundle && shouldRenameBundle) {
        const { exists: bundleTargetExists } = await engine.workspaceExists({
          path: pairedBundle.to,
        });
        if (bundleTargetExists) {
          get().setStatusMessage(
            tr("workspace.status.targetBundleExists", { name: fileNameFromPath(pairedBundle.to) }),
            3000,
          );
          return false;
        }
      }
      const referenceOperations: WorkspaceReferenceImpactOperation[] = [
        { type: "rename", from: nodePath, to: newPath, isDirectory },
      ];
      if (pairedBundle && shouldRenameBundle) {
        referenceOperations.push({
          type: "rename",
          from: pairedBundle.from,
          to: pairedBundle.to,
          isDirectory: true,
        });
      }
      const impact = await getReferenceImpact(get(), referenceOperations);
      const proceed = await confirmReferenceUpdate(get(), impact, tr("workspace.action.rename"));
      if (!proceed) return false;

      await engine.workspaceRename({ from: nodePath, to: newPath });
      if (pairedBundle && shouldRenameBundle) {
        try {
          await engine.workspaceRename({
            from: pairedBundle.from,
            to: pairedBundle.to,
          });
        } catch (bundleErr) {
          try {
            await engine.workspaceRename({ from: newPath, to: nodePath });
          } catch (rollbackErr) {
            frontendLog.error("Failed to rollback renamed document", rollbackErr, {
              source: "system",
            });
          }
          throw bundleErr;
        }
      }
      const { tabs } = get();
      const updatedTabs = tabs.map((t) => {
        if (t.filePath === nodePath) {
          return { ...t, filePath: newPath, title: newName };
        }
        return t;
      });
      set({ tabs: updatedTabs });
      const updatedReferences = impact
        ? await applyReferenceRewrites(get(), impact.references)
        : await applyLegacyAssetRenameReferences(get(), nodePath, newPath);
      if (parentDir) {
        await get().refreshSubtree(parentDir);
      } else {
        await get().refreshFileTree();
      }
      get().setStatusMessage(
        updatedReferences > 0
          ? tr("workspace.status.renamedWithReferences", { name: newName, references: updatedReferences })
          : tr("workspace.status.renamed", { name: newName }),
        2000,
      );
      return true;
    } catch (err) {
      frontendLog.error("Failed to rename", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.renameFailed"));
      return false;
    }
  },

  copyPathToClipboard: async (nodePath: string) => {
    try {
      await navigator.clipboard.writeText(nodePath);
      get().setStatusMessage(tr("workspace.status.copyPathSucceeded"), 2000);
    } catch {
      get().setStatusMessage(tr("workspace.status.copyPathFailed"));
    }
  },

  setClipboardSource: (nodePath: string, isDirectory: boolean) => {
    set({ _clipboardSource: { path: nodePath, isDirectory } });
    get().setStatusMessage(tr("workspace.status.copied", { name: fileNameFromPath(nodePath) }), 2000);
  },

  pasteInDir: async (targetDir: string) => {
    const source = get()._clipboardSource;
    if (!source) {
      get().setStatusMessage(tr("workspace.status.clipboardEmpty"), 2000);
      return;
    }
    const name = fileNameFromPath(source.path);
    try {
      const engine = getPlatformService().engine;
      let destPath = `${targetDir}/${name}`;
      let counter = 0;
      while ((await engine.workspaceExists({ path: destPath })).exists) {
        counter++;
        const dotIdx = name.lastIndexOf(".");
        if (dotIdx > 0 && !source.isDirectory) {
          const base = name.substring(0, dotIdx);
          const ext = name.substring(dotIdx);
          destPath = `${targetDir}/${base} (${counter})${ext}`;
        } else {
          destPath = `${targetDir}/${name} (${counter})`;
        }
      }
      await engine.workspaceCopy({ from: source.path, to: destPath });
      await get().refreshSubtree(targetDir);
      const finalName = fileNameFromPath(destPath);
      get().setStatusMessage(tr("workspace.status.pasted", { name: finalName }), 2000);
    } catch (err) {
      frontendLog.error("Failed to paste", err, { source: "system" });
      get().setStatusMessage(tr("workspace.status.pasteFailed"));
    }
  },

  moveNodes: async (sourcePaths: string[], targetDir: string) => {
    let movedCount = 0;
    const affectedDirs = new Set<string>();
    affectedDirs.add(targetDir);
    const engine = getPlatformService().engine;
    const plannedMoves: PlannedMove[] = [];
    const plannedTargets = new Set<string>();
    const failures: Array<{ name: string; message: string }> = [];

    for (const srcPath of sourcePaths) {
      const name = fileNameFromPath(srcPath);
      const parentDir = srcPath.substring(0, srcPath.lastIndexOf("/"));

      if (parentDir === targetDir) continue;
      if (targetDir.startsWith(srcPath + "/")) continue;

      let destPath = `${targetDir}/${name}`;
      let counter = 0;
      try {
        const sourceStat = await engine.workspaceStat({ path: srcPath }).catch(() => null);
        const isDirectory = sourceStat?.isDirectory ?? false;
        while (
          plannedTargets.has(destPath) ||
          (await engine.workspaceExists({ path: destPath })).exists
        ) {
          counter++;
          const dotIdx = name.lastIndexOf(".");
          if (dotIdx > 0 && !isDirectory) {
            const base = name.substring(0, dotIdx);
            const ext = name.substring(dotIdx);
            destPath = `${targetDir}/${base} (${counter})${ext}`;
          } else {
            destPath = `${targetDir}/${name} (${counter})`;
          }
        }

        const pairedBundle = !isDirectory && /\.md$/i.test(srcPath)
          ? {
            from: getDocumentBundleDir(srcPath),
            to: getDocumentBundleDir(destPath),
          }
          : undefined;
        const shouldMoveBundle = pairedBundle
          ? (await engine.workspaceExists({ path: pairedBundle.from })).exists
          : false;
        if (pairedBundle && shouldMoveBundle) {
          const { exists: bundleTargetExists } = await engine.workspaceExists({
            path: pairedBundle.to,
          });
          if (bundleTargetExists || plannedTargets.has(pairedBundle.to)) {
            get().setStatusMessage(
              tr("workspace.status.targetBundleExists", { name: fileNameFromPath(pairedBundle.to) }),
              3000,
            );
            continue;
          }
        }

        plannedTargets.add(destPath);
        if (pairedBundle && shouldMoveBundle) {
          plannedTargets.add(pairedBundle.to);
        }
        plannedMoves.push({
          srcPath,
          destPath,
          name,
          parentDir,
          isDirectory,
          pairedBundle: pairedBundle && shouldMoveBundle ? pairedBundle : undefined,
        });
        affectedDirs.add(parentDir);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        failures.push({ name, message });
        frontendLog.error(`Failed to plan move ${name}`, err, { source: "system" });
      }
    }

    if (plannedMoves.length === 0) {
      if (failures.length > 0) {
        get().setStatusMessage(
          tr("workspace.status.moveFailed", { message: failures[0]?.message ?? tr("common.unknown") }),
          5000,
        );
      }
      return;
    }
    const totalMoveCount = plannedMoves.length + failures.length;

    const referenceOperations: WorkspaceReferenceImpactOperation[] = plannedMoves.flatMap((move) => {
      const operations: WorkspaceReferenceImpactOperation[] = [
        {
          type: "move",
          from: move.srcPath,
          to: move.destPath,
          isDirectory: move.isDirectory,
        },
      ];
      if (move.pairedBundle) {
        operations.push({
          type: "move",
          from: move.pairedBundle.from,
          to: move.pairedBundle.to,
          isDirectory: true,
        });
      }
      return operations;
    });
    const impact = await getReferenceImpact(get(), referenceOperations);
    const proceed = await confirmReferenceUpdate(get(), impact, tr("workspace.action.move"));
    if (!proceed) return;

    for (const move of plannedMoves) {
      try {
        const result = await engine.workspaceMove({
          sources: [move.srcPath],
          targetDir,
          autoRename: true,
        });
        const moved = result.results[0];
        if (!moved?.success) {
          throw new Error(moved?.error ?? tr("common.unknown"));
        }
        if (moved.to !== move.destPath) {
          try {
            await engine.workspaceRename({ from: moved.to, to: move.srcPath });
          } catch (rollbackErr) {
            frontendLog.error("Failed to rollback move with changed destination", rollbackErr, {
              source: "system",
            });
          }
          throw new Error(
            tr("workspace.status.moveDestinationChanged", {
              expected: move.destPath,
              actual: moved.to,
            }),
          );
        }
        if (move.pairedBundle) {
          try {
            const [sourceBundle, targetBundle] = await Promise.all([
              engine.workspaceExists({ path: move.pairedBundle.from }),
              engine.workspaceExists({ path: move.pairedBundle.to }),
            ]);
            if (sourceBundle.exists && !targetBundle.exists) {
              await engine.workspaceRename({
                from: move.pairedBundle.from,
                to: move.pairedBundle.to,
              });
            } else if (sourceBundle.exists === targetBundle.exists) {
              throw new Error(
                tr("workspace.status.bundleMoveStateChanged", { name: move.name }),
              );
            }
            // An open Engine session migrates its external-output bundle when
            // workspace/move updates the document path. If only the target
            // exists, the bundle is already aligned and must not be moved a
            // second time.
          } catch (bundleErr) {
            try {
              await engine.workspaceRename({ from: move.destPath, to: move.srcPath });
            } catch (rollbackErr) {
              frontendLog.error("Failed to rollback moved document", rollbackErr, {
                source: "system",
              });
            }
            throw bundleErr;
          }
        }
        movedCount++;

        const { tabs } = get();
        const updatedTabs = tabs.map((t) => {
          if (t.filePath === move.srcPath) {
            return {
              ...t,
              filePath: move.destPath,
              title: fileNameFromPath(move.destPath),
            };
          }
          if (t.filePath?.startsWith(move.srcPath + "/")) {
            const newFilePath = move.destPath + t.filePath.substring(move.srcPath.length);
            return { ...t, filePath: newFilePath };
          }
          return t;
        });
        set({ tabs: updatedTabs });
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        failures.push({ name: move.name, message });
        frontendLog.error(`Failed to move ${move.name}`, err, { source: "system" });
      }
    }

    let updatedReferences = 0;
    let referenceUpdateFailure: string | null = null;
    if (impact && movedCount === plannedMoves.length) {
      try {
        updatedReferences = await applyReferenceRewrites(get(), impact.references);
      } catch (err) {
        referenceUpdateFailure = err instanceof Error ? err.message : String(err);
        frontendLog.error("Failed to update references after move", err, { source: "system" });
      }
    }

    await Promise.all(Array.from(affectedDirs, (dir) => get().refreshSubtree(dir)));

    if (failures.length > 0) {
      const failure = failures[0];
      get().setStatusMessage(
        movedCount > 0
          ? tr("workspace.status.movePartial", {
            moved: movedCount,
            total: totalMoveCount,
            message: `${failure?.name ?? ""}: ${failure?.message ?? tr("common.unknown")}`,
          })
          : tr("workspace.status.moveFailed", {
            message: `${failure?.name ?? ""}: ${failure?.message ?? tr("common.unknown")}`,
          }),
        5000,
      );
    } else if (referenceUpdateFailure) {
      get().setStatusMessage(
        tr("workspace.status.movedReferenceUpdateFailed", {
          count: movedCount,
          message: referenceUpdateFailure,
        }),
        5000,
      );
    } else if (movedCount > 0) {
      get().setStatusMessage(
        updatedReferences > 0
          ? tr("workspace.status.movedWithReferences", { count: movedCount, references: updatedReferences })
          : movedCount === 1
          ? tr("workspace.status.moved", { name: plannedMoves[0]?.name ?? fileNameFromPath(sourcePaths[0]) })
          : tr("workspace.status.movedMany", { count: movedCount }),
        2000,
      );
    }
  },

  refreshSubtree: async (dirPath: string) => {
    const { fileTree, workspaceFolders } = get();
    if (workspaceFolders.length === 0) return;

    function hasBundlePathTarget(nodes: FileTreeNode[], target: string): boolean {
      for (const node of nodes) {
        if (node.bundlePath === target) return true;
        if ((node.isDirectory || node.bundlePath) && node.children?.length) {
          if (hasBundlePathTarget(node.children, target)) return true;
        }
      }
      return false;
    }

    function hasRefreshTarget(nodes: FileTreeNode[]): boolean {
      for (const node of nodes) {
        if (
          (node.isDirectory && node.path === dirPath) ||
          node.bundlePath === dirPath
        ) {
          return true;
        }
        if ((node.isDirectory || node.bundlePath) && node.children?.length) {
          if (hasRefreshTarget(node.children)) return true;
        }
      }
      return false;
    }

    if (!hasRefreshTarget(fileTree)) {
      const parts = dirPath.split("/");
      const magicIdx = parts.findIndex((p) => p.endsWith(".magic-md"));
      if (magicIdx >= 0) {
        // Try the .magic-md path directly — hasRefreshTarget will match
        // a tree node whose bundlePath equals this value (e.g. xx.md with
        // bundlePath = xx.magic-md).  This refreshes only the owning
        // document node instead of the entire parent directory.
        const magicMdPath = parts.slice(0, magicIdx + 1).join("/") || "/";
        if (hasBundlePathTarget(fileTree, magicMdPath)) {
          return get().refreshSubtree(magicMdPath);
        }
        // Fallback: refresh the parent directory containing the .magic-md dir.
        const parentDir = parts.slice(0, magicIdx).join("/");
        if (parentDir) {
          return get().refreshSubtree(parentDir);
        }
      }
    }

    try {
      const engine = getPlatformService().engine;
      const publishBundlePath = getBundleDirFromPublishDirPath(dirPath);
      const isPublishPath = isDocumentPublishDirPath(dirPath);
      const isAssetsPath = isDocumentAssetsPath(dirPath);
      const isBundlePath = /\.magic-md$/i.test(dirPath);
      const entries = isPublishPath || isAssetsPath || isBundlePath
        ? await safeReadDir(engine, dirPath)
        : (await engine.workspaceReadDir({ path: dirPath })).entries;
      const folderPublishEntries = isPublishPath || isAssetsPath || isBundlePath
        ? []
        : await safeReadDir(engine, `${dirPath}/.magic-md/publish`);

      function updateNode(nodes: FileTreeNode[]): FileTreeNode[] {
        return nodes.map((node) => {
          if (node.path === dirPath && node.isDirectory) {
            let newChildren: FileTreeNode[];
            if (node.isVirtual) {
              if (isAssetsPath) {
                newChildren = buildVirtualChildren(dirPath, entries);
              } else if (
                isPublishPath &&
                isFolderPublishDirPath(dirPath)
              ) {
                newChildren = buildFolderPublishVirtualChildren(
                  dirPath,
                  entries,
                );
              } else if (isPublishPath && publishBundlePath) {
                newChildren = buildPublishVirtualChildren(
                  publishBundlePath,
                  entries,
                );
              } else {
                newChildren = buildVirtualChildren(dirPath, entries);
              }
            } else {
              newChildren = buildVisibleChildren(dirPath, entries);
              // Inject virtual publish node for folder-level publish
              const magicMdPublishPath = `${dirPath}/.magic-md/publish`;
              if (hasFolderPublishMetadataEntries(folderPublishEntries)) {
                newChildren = newChildren.filter(
                  (c) => c.name !== ".magic-md",
                );
                newChildren.push({
                  name: "publish",
                  path: magicMdPublishPath,
                  isDirectory: true,
                  isVirtual: true,
                  children: [],
                  loaded: false,
                  expanded: false,
                });
                newChildren.sort((a, b) => {
                  if (a.isDirectory !== b.isDirectory)
                    return a.isDirectory ? -1 : 1;
                  return a.name.localeCompare(b.name);
                });
              }
            }
            const existingMap = new Map(
              (node.children ?? []).map((c) => [c.name, c]),
            );
            const merged = newChildren.map((nc) => {
              const existing = existingMap.get(nc.name);
              if (
                existing &&
                isExpandableNode(existing) &&
                isExpandableNode(nc)
              ) {
                return {
                  ...nc,
                  expanded: existing.expanded,
                  loaded: existing.loaded,
                  children: existing.loaded ? existing.children : nc.children,
                };
              }
              return nc;
            });
            return {
              ...node,
              children: merged,
              loaded: true,
              expanded: node.expanded,
            };
          }
          if (node.bundlePath === dirPath) {
            const newChildren = buildBundleVirtualChildren(dirPath, entries);
            const existingMap = new Map(
              (node.children ?? []).map((child) => [child.name, child]),
            );
            const merged = newChildren.map((nextChild) => {
              const existing = existingMap.get(nextChild.name);
              if (
                existing &&
                isExpandableNode(existing) &&
                isExpandableNode(nextChild)
              ) {
                return {
                  ...nextChild,
                  expanded: existing.expanded,
                  loaded: existing.loaded,
                  children: existing.loaded
                    ? existing.children
                    : nextChild.children,
                };
              }
              return nextChild;
            });
            return {
              ...node,
              children: merged,
              loaded: true,
              expanded: node.expanded,
            };
          }
          if ((node.isDirectory || node.bundlePath) && node.children) {
            return { ...node, children: updateNode(node.children) };
          }
          return node;
        });
      }

      // Apply the fetched directory snapshot to the latest tree. File watcher
      // events can refresh source and target directories concurrently during a
      // move; writing a tree captured before the await would resurrect stale
      // source nodes or discard freshly-added target nodes.
      set((state) => ({ fileTree: updateNode(state.fileTree) }));
    } catch (err) {
      frontendLog.error("Failed to refresh subtree", err, { source: "system" });
    }
  },
});

function collectSessionState(get: () => EditorState): WorkspaceSessionState {
  const state = get();
  const persistableTabs = state.tabs.filter(
    (tab) =>
      Boolean(tab.filePath) &&
      (tab.kind === undefined || tab.kind === "document") &&
      !tab.filePath?.toLowerCase().endsWith(".mdspace"),
  );
  const tabIdToPath = new Map<string, string>();
  for (const t of persistableTabs) {
    if (t.filePath) tabIdToPath.set(t.id, t.filePath);
  }
  const tabs = persistableTabs
    .map((t) => ({ path: t.filePath as string }));
  const activeTab = state.tabs.find((t) => t.id === state.activeTabId);
  const activeTabPath = activeTab && tabIdToPath.has(activeTab.id)
    ? activeTab.filePath
    : undefined;

  // Capture panes (only those with at least one file-backed tab).
  let sessionPanes: SessionPaneState[] | undefined;
  let sessionLayout: SessionPaneLayout | null | undefined;
  let focusedPaneIndex: number | undefined;

  if (Array.isArray(state.panes) && state.panes.length > 0) {
    const paneIdToIndex = new Map<string, number>();
    sessionPanes = [];
    for (const p of state.panes) {
      const tabPaths = p.tabIds
        .map((id) => tabIdToPath.get(id))
        .filter((x): x is string => Boolean(x));
      if (tabPaths.length === 0) continue;
      paneIdToIndex.set(p.paneId, sessionPanes.length);
      sessionPanes.push({
        tabPaths,
        activeTabPath: tabIdToPath.get(p.tabId),
        viewMode: p.viewMode,
        splitPercent: p.splitPercent,
        previewWidth: normalizePreviewWidth(p.previewWidth),
      });
    }

    const remapLayout = (
      node: import("./types").PaneLayout,
    ): SessionPaneLayout | null => {
      if (node.type === "leaf") {
        const idx = paneIdToIndex.get(node.paneId);
        if (idx === undefined) return null;
        return {
          type: "leaf",
          paneIndex: idx,
          ...(node.collapsed ? { collapsed: true } : {}),
        };
      }
      const children = node.children
        .map(remapLayout)
        .filter((x): x is SessionPaneLayout => x !== null);
      if (children.length === 0) return null;
      if (children.length === 1) return children[0];
      return {
        type: node.type,
        children,
        sizes: node.sizes.slice(0, children.length),
        ...(node.collapsed ? { collapsed: true } : {}),
      };
    };

    sessionLayout = state.paneLayout ? remapLayout(state.paneLayout) : null;
    if (state.focusedPaneId) {
      const fi = paneIdToIndex.get(state.focusedPaneId);
      if (fi !== undefined) focusedPaneIndex = fi;
    }

    if (sessionPanes.length === 0) sessionPanes = undefined;
  }

  return {
    tabs,
    activeTabPath: activeTabPath ?? undefined,
    viewMode: state.viewMode,
    splitPercent: state.splitPercent,
    previewWidth: normalizePreviewWidth(state.previewWidth),
    sidebar: {
      visible: state.sidebarOpen,
      view: state.sidebarView,
    },
    panes: sessionPanes,
    paneLayout: sessionLayout,
    focusedPaneIndex,
    explorer: {
      expandedPaths: collectExpandedTreePaths(state.fileTree),
    },
    pinnedUnits: state.pinnedUnits,
  };
}
